import { useEffect, useState } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Tag, Space, Typography, Alert, Table, Modal, App as AntApp,
  Descriptions,
} from 'antd';
import { ReloadOutlined, DownloadOutlined, EyeOutlined, GlobalOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { fmtDataBr } from '../../utils/dataFmt';
import { carimbarBlob, baixarFotoCarimbada, nomeCarimbado } from '../../utils/fotoCarimbada';
import type { Contrato } from './types';

const { Title, Text } = Typography;

// Uma medida do dia. O dia de cadastro rende km de rede E pontos — antes so cabia um par
// {unidade, quantidade} e metade do dia se perdia.
interface MedidaProdutividade {
  unidade: string;
  quantidade: number;
  observacao?: string | null;
}

interface Diario {
  id: number;
  contrato_id: string;
  contrato_numero?: string;
  contrato_titulo?: string;
  encarregado_email: string;
  data_diario: string;
  texto_original: string;
  dados_estruturados: any;
  status: string;
  submetido_em: string | null;
  telegram_enviado_em: string | null;
  created_at: string;
}

interface FotoServer {
  id: number;
  nome_original: string | null;
  lat?: number | string | null;
  lon?: number | string | null;
  accuracy_m?: number | string | null;
  sem_geotag: boolean;
  descricao: string | null;
  diario_id: number | null;
  anotacao_de: number | null;
  email_usuario: string;
  capturado_em: string;
  url: string;
}

const STATUS_TAG: Record<string, { color: string; txt: string }> = {
  rascunho:  { color: 'default', txt: 'Rascunho' },
  completo:  { color: 'blue', txt: 'Pronto p/ envio' },
  submetido: { color: 'success', txt: 'Submetido' },
  aprovado:  { color: 'success', txt: 'Aprovado' },
  reprovado: { color: 'error',   txt: 'Reprovado' },
};

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

function AbaDiariosCampoInner({ contrato }: { contrato: Contrato }) {
  const { message } = AntApp.useApp();
  const [diarios, setDiarios] = useState<Diario[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [selecionado, setSelecionado] = useState<Diario | null>(null);
  const [fotos, setFotos] = useState<FotoServer[]>([]);
  const [fotosContrato, setFotosContrato] = useState<FotoServer[]>([]);
  const [baixandoZip, setBaixandoZip] = useState(false);
  const [baixandoGeoJson, setBaixandoGeoJson] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/mobile/contratos/${contrato.id}/fotos`, { headers: { Authorization: token } });
        const raw: any = r.data;
        const data: any = raw?.body ? JSON.parse(raw.body) : raw;
        setFotosContrato(data?.fotos || []);
      } catch { setFotosContrato([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrato.id]);

  async function baixarGeoJson() {
    setBaixandoGeoJson(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/mobile/contratos/${contrato.id}/fotos-geojson`, {
        headers: { Authorization: token },
      });
      const raw: any = r.data;
      const data: any = raw?.body ? JSON.parse(raw.body) : raw;
      if (!data?.features?.length) {
        message.warning('Nenhuma foto com geotag neste contrato/aditivos');
        return;
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/geo+json' });
      saveAs(blob, `fotos-${contrato.numero}.geojson`);
      message.success(`${data.features.length} pontos exportados`);
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally {
      setBaixandoGeoJson(false);
    }
  }

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [contrato.id]);

  async function carregar() {
    setLoading(true);
    setErro(null);
    try {
      const token = await getToken();
      const r = await axios.get<{ diarios: Diario[] }>(`${API_URL}/mobile/diarios`, {
        headers: { Authorization: token },
        params: { contrato_id: contrato.id },
      });
      setDiarios(r.data.diarios || []);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }

  async function abrir(d: Diario) {
    setSelecionado(d);
    setModalAberto(true);
    setFotos([]);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/mobile/diarios/${d.id}/fotos`, { headers: { Authorization: token } });
      const raw: any = r.data;
      const data: any = raw?.body ? JSON.parse(raw.body) : raw;
      setFotos(data?.fotos || []);
    } catch { setFotos([]); }
  }

  // comCarimbo: imprime data/hora + coordenadas (metadados do banco) em cada
  // foto NA HORA DO DOWNLOAD — a foto armazenada continua limpa.
  async function baixarZip(comCarimbo: boolean) {
    if (!selecionado || !fotos.length) return;
    setBaixandoZip(true);
    try {
      const zip = new JSZip();
      let ok = 0, falhas = 0;
      for (let i = 0; i < fotos.length; i++) {
        const f = fotos[i];
        try {
          const resp = await fetch(f.url, { mode: 'cors', cache: 'no-store' });
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          let blob = await resp.blob();
          let nome = f.nome_original || `foto-${String(i + 1).padStart(2, '0')}.jpg`;
          if (comCarimbo) {
            try {
              blob = await carimbarBlob(blob, f);
              nome = nomeCarimbado(nome, `foto-${String(i + 1).padStart(2, '0')}`);
            } catch { /* carimbo falhou nessa foto — entra sem carimbo */ }
          }
          zip.file(nome, blob);
          ok++;
        } catch { falhas++; }
      }
      if (ok === 0) {
        message.error('Não consegui baixar nenhuma foto. Se você atualizou o sistema recentemente, dê Ctrl+Shift+R (ou limpe os dados do site) e tente de novo.');
        return;
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, `diario-${selecionado.id}-${contrato.numero}-${String(selecionado.data_diario).slice(0, 10)}${comCarimbo ? '-carimbadas' : ''}.zip`);
      if (falhas > 0) message.warning(`${ok} foto(s) no zip; ${falhas} não baixaram.`);
    } catch (e: any) {
      message.error('Falha no zip: ' + e.message);
    } finally {
      setBaixandoZip(false);
    }
  }

  function baixarUmaCarimbada(f: FotoServer) {
    baixarFotoCarimbada(f).catch((e) => message.error('Falha ao baixar: ' + (e as Error).message));
  }

  const columns = [
    { title: 'Data', dataIndex: 'data_diario', width: 110,
      render: (v: string) => fmtDataBr(v) },
    { title: 'Atividade', key: 'atividade',
      render: (_: any, d: Diario) => d.dados_estruturados?.atividade?.descricao || <Text type="secondary">—</Text> },
    { title: 'Produção', key: 'prod', width: 160,
      render: (_: any, d: Diario) => {
        // `dados_estruturados` e `any` nesta tela — anotar o item evita o TS7006.
        const itens: MedidaProdutividade[] = d.dados_estruturados?.produtividade_itens || [];
        if (itens.length) return itens.map((m) => `${m.quantidade} ${m.unidade}`).join(' · ');
        const p = d.dados_estruturados?.produtividade;
        return p?.quantidade != null ? `${p.quantidade} ${p.unidade || ''}` : '—';
      } },
    { title: 'Encarregado', dataIndex: 'encarregado_email',
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text> },
    { title: 'Status', dataIndex: 'status', width: 140,
      render: (s: string) => {
        const t = STATUS_TAG[s] || { color: 'default', txt: s };
        return <Tag color={t.color}>{t.txt}</Tag>;
      } },
    { title: '', key: 'acoes', width: 100,
      render: (_: any, d: Diario) => <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => abrir(d)}>Ver</Button> },
  ];

  return (
    <Card style={{ marginTop: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Title level={4} style={{ margin: 0 }}>📝 Diários — {contrato.numero}</Title>
          <Space>
            <Button icon={<GlobalOutlined />} onClick={baixarGeoJson} loading={baixandoGeoJson}>
              Baixar GeoJSON das fotos
            </Button>
            <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>Atualizar</Button>
          </Space>
        </div>

        {erro && <Alert type="error" message={erro} />}

        <Table
          rowKey="id"
          dataSource={diarios}
          columns={columns as any}
          loading={loading}
          pagination={{ pageSize: 50 }}
          locale={{ emptyText: 'Nenhum diário lançado pra este contrato ainda' }}
        />

        {/* Galeria de TODAS as fotos do contrato (diários + avulsas + anotações) */}
        {fotosContrato.length > 0 && (
          <Card size="small" title={`📷 Todas as fotos do contrato (${fotosContrato.length})`}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
              {fotosContrato.map((f) => (
                <a key={f.id} href={f.url} target="_blank" rel="noreferrer" style={{ position: 'relative', display: 'block' }}>
                  <img src={f.url} alt={f.nome_original || ''} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                  {f.sem_geotag && (
                    <span style={{ position: 'absolute', top: 4, left: 4, background: '#f59e0b', color: 'white', fontSize: 9, padding: '1px 4px', borderRadius: 3, fontWeight: 600 }}>
                      sem GPS
                    </span>
                  )}
                  {f.anotacao_de && (
                    <span style={{ position: 'absolute', top: 4, right: 4, background: '#2563eb', color: 'white', fontSize: 9, padding: '1px 4px', borderRadius: 3, fontWeight: 600 }}>
                      ✏️ croqui
                    </span>
                  )}
                  {!f.diario_id && !f.anotacao_de && (
                    <span style={{ position: 'absolute', bottom: 4, right: 4, background: '#6b7280', color: 'white', fontSize: 9, padding: '1px 4px', borderRadius: 3, fontWeight: 600 }}>
                      avulsa
                    </span>
                  )}
                  {f.descricao && (
                    <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 10, padding: '2px 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.descricao}
                    </span>
                  )}
                  <span
                    title="Baixar com carimbo de data/hora e coordenadas"
                    onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); baixarUmaCarimbada(f); }}
                    style={{ position: 'absolute', top: 24, right: 4, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 600, cursor: 'pointer' }}
                  >🕒⬇</span>
                </a>
              ))}
            </div>
          </Card>
        )}
      </Space>

      <Modal
        open={modalAberto}
        onCancel={() => setModalAberto(false)}
        title={selecionado ? `Diário #${selecionado.id} — ${fmtDataBr(selecionado.data_diario)}` : ''}
        width={720}
        footer={[<Button key="close" onClick={() => setModalAberto(false)}>Fechar</Button>]}
      >
        {selecionado && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Encarregado">{selecionado.encarregado_email}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_TAG[selecionado.status]?.color}>{STATUS_TAG[selecionado.status]?.txt || selecionado.status}</Tag>
              </Descriptions.Item>
              {(selecionado.dados_estruturados?.local?.cidade || selecionado.dados_estruturados?.local?.especifico) && (
                <Descriptions.Item label="Local" span={2}>
                  {[selecionado.dados_estruturados.local?.cidade, selecionado.dados_estruturados.local?.especifico].filter(Boolean).join(' — ')}
                </Descriptions.Item>
              )}
              {selecionado.dados_estruturados?.atividade?.descricao && (
                <Descriptions.Item label="Atividade" span={2}>
                  {selecionado.dados_estruturados.atividade.descricao}
                </Descriptions.Item>
              )}
              {selecionado.dados_estruturados?.produtividade?.quantidade != null && (
                <Descriptions.Item label="Produção" span={2}>
                  {selecionado.dados_estruturados.produtividade.quantidade} {selecionado.dados_estruturados.produtividade.unidade || ''}
                </Descriptions.Item>
              )}
              {(selecionado.dados_estruturados?.obices?.length ?? 0) > 0 && (
                <Descriptions.Item label="Óbices" span={2}>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {selecionado.dados_estruturados.obices.map((o: any, i: number) => (
                      <li key={i}><strong>{o.categoria}:</strong> {o.descricao}</li>
                    ))}
                  </ul>
                </Descriptions.Item>
              )}
            </Descriptions>

            {selecionado.texto_original && (
              <Card size="small" title="Texto original">
                <Text italic style={{ whiteSpace: 'pre-wrap' }}>"{selecionado.texto_original}"</Text>
              </Card>
            )}

            <Card
              size="small"
              title={`📷 Fotos (${fotos.length})`}
              extra={fotos.length > 0 && (
                <Space size={4}>
                  <Button
                    size="small"
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => baixarZip(true)}
                    loading={baixandoZip}
                    title="Cada foto sai com data/hora e coordenadas impressas"
                  >
                    Baixar carimbadas
                  </Button>
                  <Button size="small" onClick={() => baixarZip(false)} loading={baixandoZip}>
                    sem carimbo
                  </Button>
                </Space>
              )}
            >
              {fotos.length === 0 ? <Text type="secondary">Nenhuma foto anexada.</Text> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                  {fotos.map((f) => (
                    <a key={f.id} href={f.url} target="_blank" rel="noreferrer" style={{ position: 'relative', display: 'block' }}>
                      <img src={f.url} alt={f.nome_original || ''} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                      {f.sem_geotag && (
                        <span style={{ position: 'absolute', top: 4, left: 4, background: '#f59e0b', color: 'white', fontSize: 9, padding: '1px 4px', borderRadius: 3, fontWeight: 600 }}>
                          sem GPS
                        </span>
                      )}
                      <span
                        title="Baixar com carimbo de data/hora e coordenadas"
                        onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); baixarUmaCarimbada(f); }}
                        style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 600, cursor: 'pointer' }}
                      >🕒⬇</span>
                    </a>
                  ))}
                </div>
              )}
            </Card>
          </Space>
        )}
      </Modal>
    </Card>
  );
}

export function AbaDiariosCampo({ contrato }: { contrato: Contrato }) {
  return <AntApp><AbaDiariosCampoInner contrato={contrato} /></AntApp>;
}
